/**
 * Centralized Dialog Messages
 *
 * All dialog titles, messages, and labels are defined here for easy updates.
 * This is the single source of truth for user-facing dialog text.
 */

import { TagActionType } from "~/types/character";

/**
 * Damage Dialog Messages
 */
export const DAMAGE_DIALOG = {
  title: 'Apply Damage',
  message: 'Damage is applied to Resilience first, then Health Points.',
  inputLabel: 'Damage Amount',
  confirmLabel: 'Apply',
  theme: 'negative' as const,
};

/**
 * Healing Dialog Messages
 */
export const HEAL_DIALOG = {
  title: 'Apply Healing',
  message: 'Restore Health Points.',
  inputLabel: 'Healing Amount',
  confirmLabel: 'Heal',
  theme: 'positive' as const,
};

/**
 * Direct Damage Dialog Messages
 */
export const DIRECT_DAMAGE_DIALOG = {
  title: 'Apply Direct Damage',
  message: 'Direct damage bypasses Resilience and goes straight to your HP.',
  inputLabel: 'Damage Amount',
  confirmLabel: 'Apply',
  theme: 'negative' as const,
};

/**
 * Spend Resilience Dialog Messages
 */
export const SPEND_RESILIENCE_DIALOG = {
  title: 'Spend Resilience',
  message: '',
  inputLabel: 'Resilience Amount',
  confirmLabel: 'Spend',
  theme: 'negative' as const,
};

/**
 * Delete Character Dialog Messages
 */
export const DELETE_CHARACTER_DIALOG = {
  title: 'Delete Character',
  confirmLabel: 'Delete',
  theme: 'negative' as const,
  getMessage: (characterName: string) =>
    `Are you sure you want to delete "${characterName}"? This action cannot be undone.`,
};

/**
 * Tag Action Dialog Messages
 *
 * Returns dialog configuration for tag action confirmations
 */
export const getTagActionDialog = (
  type: TagActionType,
  value: number,
  sourceName?: string,
  sourceType?: string
) => {
  const sourceLabel = sourceType ? `${sourceType}: ${sourceName}` : sourceName || 'Unknown';

  switch (type) {
    case TagActionType.GAIN_HEALTH:
      return {
        title: 'Gain Health',
        sourceLabel,
        message: `Restore **${value} Health Points**.`,
        confirmLabel: 'Apply',
        theme: 'positive' as const,
      };

    case TagActionType.LOSE_HEALTH:
      return {
        title: 'Lose Health',
        sourceLabel,
        message: `Take **${value} damage** directly to Health Points.`,
        confirmLabel: 'Apply',
        theme: 'negative' as const,
      };

    case TagActionType.LOSE_RESILIENCE:
      return {
        title: 'Use Resilience',
        sourceLabel,
        message: `Spend **${value} Resilience** to activate this ${sourceType?.toLowerCase() || 'effect'}.`,
        confirmLabel: 'Use',
        theme: 'negative' as const,
      };

    case TagActionType.ACQUIRE_STRAIN:
      return {
        title: 'Acquire Strain',
        sourceLabel,
        message: `Gain **${value} Strain**. Remember to apply strain damage at the end of combat.`,
        confirmLabel: 'Apply',
        theme: 'negative' as const,
      };

    case TagActionType.REDUCE_STRAIN:
      return {
        title: 'Reduce Strain',
        sourceLabel,
        message: `Reduce Strain by **${value}**.`,
        confirmLabel: 'Apply',
        theme: 'positive' as const,
      };

    default:
      return {
        title: 'Confirm Action',
        sourceLabel,
        message: `Apply this effect?`,
        confirmLabel: 'Confirm',
        theme: 'neutral' as const,
      };
  }
};
